import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import {
  clearMustChangePasswordState,
  mustChangePasswordPending,
  navigateToAppDestination,
  readPostLoginContext,
} from "../../utils/postLoginRedirect";

function SetPassword() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const passwordsMatch = confirmPassword && newPassword === confirmPassword;

  const validatePassword = (password) => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&]/.test(password),
    });
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!mustChangePasswordPending()) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("All fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (!/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(newPassword)) {
      toast.error(
        "Password must be at least 8 characters, include an uppercase letter, number, and special symbol.",
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/set-initial-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        const context = readPostLoginContext();
        clearMustChangePasswordState();
        sessionStorage.removeItem("auth_last_profile_check");
        sessionStorage.removeItem("auth_last_authorized");
        window.dispatchEvent(new Event("login"));
        navigateToAppDestination(context, navigate, {
          toast,
          toastId: "set-password-success",
          successMessage: "Password set successfully. Welcome!",
        });
      } else {
        toast.error(data.message || "Failed to set password");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordInput = (label, value, setValue, show, setShow, placeholder, autoComplete) => (
    <div>
      <label
        className="block mb-2"
        style={{
          font: "600 12px 'Sora', sans-serif",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--an-slate)",
        }}
      >
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <LockClosedIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={show ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          onChange={(e) => setValue(e.target.value)}
          className="block w-full pl-10 pr-12 focus:outline-none focus:ring-2 focus:ring-[color:rgba(255,122,102,.45)] transition-colors"
          style={{
            background: "var(--an-mist)",
            border: "none",
            borderRadius: 12,
            padding: "14px 15px 14px 40px",
            color: "var(--an-ink)",
            font: "400 15px 'Sora', sans-serif",
          }}
          placeholder={placeholder}
          required
          disabled={loading}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow(!show)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6" style={{ background: "var(--an-white)" }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: "var(--an-ink)" }}
          >
            <ShieldCheckIcon className="h-7 w-7" style={{ color: "var(--an-coral)" }} />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: "var(--an-ink)", fontFamily: "'Fraunces', Georgia, serif" }}>
            Set your password
          </h1>
          <p className="text-sm" style={{ color: "var(--an-slate)" }}>
            For security, replace the temporary password from your welcome email before continuing.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-8 rounded-2xl"
          style={{ background: "var(--an-paper)", border: "1px solid var(--an-hair)" }}
        >
          {renderPasswordInput(
            "Temporary password",
            currentPassword,
            setCurrentPassword,
            showCurrent,
            setShowCurrent,
            "Enter the password from your email",
            "current-password",
          )}

          <div>
            <label
              className="block mb-2"
              style={{
                font: "600 12px 'Sora', sans-serif",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--an-slate)",
              }}
            >
              New password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockClosedIcon className="h-5 w-5" style={{ color: "var(--an-silver)" }} />
              </div>
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                className="block w-full pl-10 pr-12 focus:outline-none focus:ring-2 focus:ring-[color:rgba(255,122,102,.45)] transition-colors"
                style={{
                  background: "var(--an-mist)",
                  border: "none",
                  borderRadius: 12,
                  padding: "14px 15px 14px 40px",
                  color: "var(--an-ink)",
                  font: "400 15px 'Sora', sans-serif",
                }}
                placeholder="Create a new password"
                required
                disabled={loading}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowNew(!showNew)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showNew ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {newPassword && (
            <div className="rounded-lg p-4" style={{ background: "var(--an-mist)", border: "1px solid var(--an-hair)" }}>
              <div className="space-y-2">
                {[
                  ["minLength", "At least 8 characters"],
                  ["hasUppercase", "Uppercase letter (A-Z)"],
                  ["hasNumber", "Number (0-9)"],
                  ["hasSpecialChar", "Special character (@$!%*?&)"],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    {passwordValidation[key] ? (
                      <CheckCircleIcon className="h-4 w-4" style={{ color: "var(--an-graphite)" }} />
                    ) : (
                      <XMarkIcon className="h-4 w-4" style={{ color: "var(--an-silver)" }} />
                    )}
                    <span className="text-xs" style={{ color: "var(--an-slate)" }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {renderPasswordInput(
            "Confirm new password",
            confirmPassword,
            setConfirmPassword,
            showConfirm,
            setShowConfirm,
            "Repeat your new password",
            "new-password",
          )}

          {confirmPassword && (
            <div
              className="rounded-lg p-3"
              style={{
                background: "var(--an-mist)",
                border: passwordsMatch ? "1px solid var(--an-success)" : "1px solid var(--an-danger)",
              }}
            >
              <div className="flex items-center gap-2">
                {passwordsMatch ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5" style={{ color: "var(--an-success)" }} />
                    <span className="text-sm font-medium" style={{ color: "var(--an-success)" }}>
                      Passwords match
                    </span>
                  </>
                ) : (
                  <>
                    <XMarkIcon className="h-5 w-5" style={{ color: "var(--an-danger)" }} />
                    <span className="text-sm font-medium" style={{ color: "var(--an-danger)" }}>
                      Passwords do not match
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-xl font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: "var(--an-coral)" }}
          >
            {loading ? "Saving password…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SetPassword;
